import { HttpClient } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs';

import { DiscussionForumTopicHistory, DiscussionForum, DiscussionForumTopic, DiscussionForumTopicMessage } from '@empathy/global/data';

import { EnvironmentConfig } from '../config/environment.config';
import { DiscussionForumParticipantDetail } from '../models/discussion-forum/discussion-forum-participant-detail.interface';
import { DiscussionForumParticipant } from '../models/discussion-forum/discussion-forum-participant.interface';
import { DiscussionForumStats } from '../models/discussion-forum/discussion-forum-stats.interface';
import { DiscussionParticipantFlagType } from '../models/discussion-forum/discussion-participant-flag-type.enum';
import { DiscussionParticipantProfile } from '../models/discussion-forum/discussion-participant-profile.interface';
import { DiscussionTopicParticipant } from '../models/discussion-forum/discussion-topic-participant.interface';
import { DiscussionParticipantLabel } from '../models/discussion-forum/participant-label.model';
import { DiscussionForumTopicMessageFeedbackResponse } from '../models/discussion-forum/topic-message-feedback-response.interface';
import { DiscussionForumTopicMessageFeedbackUpdate } from '../models/discussion-forum/topic-message-feedback-update.interface';
import { DiscussionForumTopicNewMessage } from '../models/discussion-forum/topic-new-message.interface';
import { IParticipantSettings } from '../models/settings/participant-settings.interface';
import { ISchedule } from '../models/settings/schedule.interface';

@Injectable({
  providedIn: 'root'
})
export class DiscussionForumService {
  constructor(
    @Inject('environment') private environment: EnvironmentConfig,
    private http: HttpClient
  ) {}

  getDiscussionForumTopicHistory(projectId: string, methodId: string, topicId: string): Observable<DiscussionForumTopicHistory> {
    return this.http.get<DiscussionForumTopicHistory>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic/${topicId}/history`
    );
  }

  editDiscussionForum(projectId: string, methodId: string, forumDetails: DiscussionForum): Observable<DiscussionForum> {
    return this.http.put<DiscussionForum>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum`, forumDetails);
  }

  addDiscussionForumTopic(projectId: string, methodId: string, topic: DiscussionForumTopic): Observable<DiscussionForumTopic> {
    return this.http.post<DiscussionForumTopic>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic`, topic);
  }

  editDiscussionForumTopic(projectId: string, methodId: string, topicId: string, topic: DiscussionForumTopic): Observable<DiscussionForumTopic> {
    return this.http.put<DiscussionForumTopic>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic/${topicId}`,
      topic
    );
  }

  deleteDiscussionForumTopic(projectId: string, methodId: string, topicId: string): Observable<DiscussionForumTopic> {
    return this.http.delete<DiscussionForumTopic>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic/${topicId}`);
  }

  sendDiscussionForumMessage(newMessage: DiscussionForumTopicNewMessage): Observable<DiscussionForumTopicMessage> {
    return this.http.post<DiscussionForumTopicMessage>(
      `${this.environment.endPoint}project/${newMessage.projectId}/research/${newMessage.methodId}/discussion-forum/topic/${newMessage.topicId}`,
      newMessage
    );
  }

  getTopicParticipants(projectId: string, methodId: string, topicId: string): Observable<DiscussionTopicParticipant[]> {
    return this.http.get<DiscussionTopicParticipant[]>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic/${topicId}/participants`
    );
  }

  getTopicParticipantDetails(projectId: string, methodId: string, participantId: string): Observable<DiscussionParticipantProfile> {
    return this.http.get<DiscussionParticipantProfile>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/participants/${participantId}/details`
    );
  }

  markDiscussionForumMessageReviewed(projectId: string, methodId: string, topicId: string, responseId: string): Observable<void> {
    return this.http.patch<void>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic/${topicId}/response/${responseId}/review`,
      {}
    );
  }

  getDiscussionForumStats(projectId: string, methodId: string): Observable<DiscussionForumStats> {
    return this.http.get<DiscussionForumStats>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/stats`);
  }

  deleteDiscussionForumMessage(projectId: string, methodId: string, topicId: string, responseId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic/${topicId}/response/${responseId}`
    );
  }

  markAllDiscussionForumMessagesReviewed(projectId: string, methodId: string, topicId: string): Observable<void> {
    return this.http.put<void>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic/${topicId}/review`, {});
  }

  sendDiscussionForumMessageFeedback(
    projectId: string,
    methodId: string,
    topicId: string,
    feedback: DiscussionForumTopicMessageFeedbackUpdate
  ): Observable<DiscussionForumTopicMessageFeedbackResponse> {
    return this.http.post<DiscussionForumTopicMessageFeedbackResponse>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic/${topicId}/feedback`,
      feedback
    );
  }

  setPinnedDiscussionForumMessage(projectId: string, methodId: string, topicId: string, responseId: string | null): Observable<void> {
    return this.http.put<void>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic/${topicId}/pinned-post`, {
      responseId
    });
  }

  setDiscussionForumTopicStatus(projectId: string, methodId: string, topicId: string, isActive: boolean): Observable<void> {
    return this.http.patch<void>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic/${topicId}/status`, {
      isActive
    });
  }

  flagDiscussionForumMessage(projectId: string, methodId: string, topicId: string, responseId: string, reason: string): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic/${topicId}/response/${responseId}/flag`,
      { reason }
    );
  }

  unflagDiscussionForumMessage(projectId: string, methodId: string, topicId: string, responseId: string): Observable<boolean> {
    return this.http.delete<boolean>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/topic/${topicId}/response/${responseId}/flag`
    );
  }

  getLabels(projectId: string, methodId: string): Observable<DiscussionParticipantLabel[]> {
    return this.http.get<DiscussionParticipantLabel[]>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/labels`);
  }

  saveLabel(projectId: string, methodId: string, label: Omit<DiscussionParticipantLabel, 'id'>): Observable<DiscussionParticipantLabel> {
    return this.http.post<DiscussionParticipantLabel>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/labels`, label);
  }

  updateLabel(projectId: string, methodId: string, id: string, label: Omit<DiscussionParticipantLabel, 'id'>): Observable<void> {
    return this.http.put<void>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/labels/${id}`, label);
  }

  deleteLabel(projectId: string, methodId: string, id: string): Observable<void> {
    return this.http.delete<void>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/labels/${id}`);
  }

  getAllParticipants(projectId: string, methodId: string): Observable<DiscussionForumParticipant[]> {
    return this.http.get<DiscussionForumParticipant[]>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/participants`);
  }

  getForumParticipantDetail(projectId: string, methodId: string, participantId: string): Observable<DiscussionForumParticipantDetail> {
    return this.http.get<DiscussionForumParticipantDetail>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/participants/${participantId}`
    );
  }

  removeParticipantFromForum(projectId: string, methodId: string, participantId: string): Observable<void> {
    return this.http.delete<void>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/participants/${participantId}`);
  }

  updateParticipantIdentity(
    projectId: string,
    methodId: string,
    participantId: string,
    identity: { name: string; emailAddress: string; respondentType: string }
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/participants/${participantId}/identity`,
      identity
    );
  }

  assignLabelToForumParticipant(projectId: string, methodId: string, participantId: string, labelIds: string[]): Observable<void> {
    return this.http.put<void>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/participants/${participantId}/labels`, {
      labelIds
    });
  }

  removeLabelFromForumParticipant(projectId: string, methodId: string, participantId: string, labelId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/participants/${participantId}/labels/${labelId}`
    );
  }

  flagForumParticipant(projectId: string, methodId: string, participantId: string, flagType: DiscussionParticipantFlagType): Observable<void> {
    return this.http.post<void>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/participants/${participantId}/flag`, {
      flagType
    });
  }

  unflagForumParticipant(projectId: string, methodId: string, participantId: string, flagType: DiscussionParticipantFlagType): Observable<void> {
    return this.http.delete<void>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/participants/${participantId}/flag`, {
      params: { flagType }
    });
  }

  saveDiscForumSettings(
    projectId: string,
    methodId: string,
    settings: { schedule: ISchedule; participantSettings: IParticipantSettings }
  ): Observable<boolean> {
    return this.http.put<boolean>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/discussion-forum/settings`, settings);
  }
}
